import type { AnimationEasing, AnimationProperty, AnimationTrack } from "../../../shared/api-contract";
import { Toggle } from "./studioControls";

/** Local MiniButton — ported from src/ui/ring/controls.tsx (which is being deleted). */
function MiniButton({ children, onClick, active }: { children: React.ReactNode; onClick: () => void; active?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition-colors ${
        active
          ? "border-ed-text bg-ed-text text-white"
          : "border-ed-line bg-ed-panel text-ed-text hover:border-ed-accent"
      }`}
    >
      {children}
    </button>
  );
}

/** Local NumberBox — ported from src/ui/ring/controls.tsx (which is being deleted). */
function NumberBox({ value, onChange, width = 64 }: { value: string; onChange: (v: string) => void; width?: number }) {
  return (
    <input
      type="number"
      step="0.01"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width }}
      className="rounded-md border border-ed-line bg-ed-panel px-1.5 py-1 text-center text-[12px] outline-none focus:border-ed-accent"
    />
  );
}

interface Props {
  track: AnimationTrack | null;
  onUpdate: (track: AnimationTrack) => void;
}

const EASINGS: AnimationEasing[] = ["linear", "easeIn", "easeOut", "easeInOut"];
const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;

function isRotation(p: AnimationProperty) {
  return p === "rotation" || p === "groupRotation";
}
function isVisibility(p: AnimationProperty) {
  return p === "visibility";
}
function isColor(p: AnimationProperty) {
  return p === "gemColor" || p === "metalColor";
}
/** Single-number 0..1 material scalars. */
function isScalar(p: AnimationProperty) {
  return p === "gemOpacity" || p === "metalRoughness" || p === "metalMetalness";
}
/** scale / groupScale / colors / boost default to 1; scalars to a sensible
 * value; everything else (offsets, rotation) to 0. */
function neutralValue(p: AnimationProperty): number[] {
  if (isVisibility(p)) return [1];
  if (p === "scale" || p === "groupScale") return [1, 1, 1];
  if (isColor(p) || p === "gemBoost") return [1, 1, 1];
  if (p === "gemOpacity" || p === "metalMetalness") return [1];
  if (p === "metalRoughness") return [0.3];
  return [0, 0, 0];
}

function fmt(n: number) {
  return Number.isFinite(n) ? String(Math.round(n * 1000) / 1000) : "0";
}

function rgbToHex(v: number[]): string {
  const to = (x: number) =>
    Math.max(0, Math.min(255, Math.round((x ?? 0) * 255)))
      .toString(16)
      .padStart(2, "0");
  return `#${to(v[0] ?? 1)}${to(v[1] ?? 1)}${to(v[2] ?? 1)}`;
}
function hexToRgb(hex: string): number[] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [1, 1, 1];
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export function KeyframeEditor({ track, onUpdate }: Props) {
  if (!track) {
    return (
      <span className="text-[12px] leading-snug text-ed-text-2">
        Select a track above to edit its keyframes.
      </span>
    );
  }

  const rot = isRotation(track.property);
  const vis = isVisibility(track.property);
  const color = isColor(track.property);
  const scalar = isScalar(track.property);

  const patchKeyframe = (i: number, patch: Partial<AnimationTrack["keyframes"][number]>) => {
    const keyframes = track.keyframes.map((k, idx) => (idx === i ? { ...k, ...patch } : k));
    onUpdate({ ...track, keyframes });
  };

  const setAxis = (i: number, axis: number, raw: string) => {
    const v = parseFloat(raw);
    const num = Number.isFinite(v) ? (rot ? v * DEG2RAD : v) : 0;
    const value = [...track.keyframes[i].value];
    while (value.length <= axis) value.push(0);
    value[axis] = num;
    patchKeyframe(i, { value });
  };

  const axisValue = (k: number[], axis: number) => {
    const raw = k[axis] ?? 0;
    return fmt(rot ? raw * RAD2DEG : raw);
  };

  const addKeyframe = () => {
    const lastT = track.keyframes.length ? track.keyframes[track.keyframes.length - 1].time : 0;
    const keyframes = [
      ...track.keyframes,
      { time: Math.round((lastT + 0.5) * 100) / 100, value: neutralValue(track.property), easing: "linear" as const },
    ];
    onUpdate({ ...track, keyframes });
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-ed-panel-2 p-2">
      <div className="flex items-center gap-2 px-0.5">
        <span className="w-12 text-[12px] font-medium text-ed-text-2">Time</span>
        <span className="flex-1 text-[12px] font-medium text-ed-text-2">
          {vis ? "State" : color ? "Colour" : scalar ? "Value (0–1)" : rot ? "Rotation (degrees)" : "X · Y · Z"}
        </span>
      </div>

      {track.keyframes.map((k, i) => (
        <div key={`${track.id}-kf-${i}`} className="flex flex-wrap items-center gap-1.5">
          <NumberBox value={fmt(k.time)} width={48} onChange={(v) => patchKeyframe(i, { time: parseFloat(v) || 0 })} />

          {vis ? (
            <div className="flex items-center gap-2">
              <Toggle
                checked={(k.value[0] ?? 1) >= 0.5}
                onChange={(on) => patchKeyframe(i, { value: [on ? 1 : 0] })}
                label="visible"
              />
              <span className="text-[11px] text-ed-text-2">
                {(k.value[0] ?? 1) >= 0.5 ? "Visible" : "Hidden"}
              </span>
            </div>
          ) : color ? (
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={rgbToHex(k.value)}
                onChange={(e) => patchKeyframe(i, { value: hexToRgb(e.target.value) })}
                className="h-7 w-10 cursor-pointer rounded-md border border-ed-line bg-transparent p-0.5"
                aria-label="Colour"
              />
              <span className="text-[12px] text-ed-text-2">
                {rgbToHex(k.value)}
              </span>
            </div>
          ) : scalar ? (
            <NumberBox value={fmt(k.value[0] ?? 0)} width={56} onChange={(v) => setAxis(i, 0, v)} />
          ) : (
            <>
              <NumberBox value={axisValue(k.value, 0)} width={46} onChange={(v) => setAxis(i, 0, v)} />
              <NumberBox value={axisValue(k.value, 1)} width={46} onChange={(v) => setAxis(i, 1, v)} />
              <NumberBox value={axisValue(k.value, 2)} width={46} onChange={(v) => setAxis(i, 2, v)} />
            </>
          )}

          <select
            value={k.easing ?? "linear"}
            onChange={(e) => patchKeyframe(i, { easing: e.target.value as AnimationEasing })}
            className="rounded-md border border-ed-line bg-ed-panel px-1.5 py-1 text-[11px] text-ed-text outline-none focus:border-ed-accent"
          >
            {EASINGS.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>

          <button
            onClick={() => onUpdate({ ...track, keyframes: track.keyframes.filter((_, idx) => idx !== i) })}
            className="rounded-md border border-ed-line px-1.5 py-1 text-[12px] font-medium text-ed-text-2 hover:border-ed-accent hover:text-ed-text"
            aria-label="Delete keyframe"
          >
            ✕
          </button>
        </div>
      ))}

      <MiniButton onClick={addKeyframe}>+ Keyframe</MiniButton>
    </div>
  );
}
